import {FC, useEffect, useState} from 'react'
import {useHistory} from 'react-router-dom'
import queryString from 'query-string'
import Button from 'jbc-front/components/Button'
import {Modal} from 'jbc-front/components/presenters/ui/Modal'
import {Balloon} from 'jbc-front/components/presenters/ui/Balloon'
import {HoverDescription, DescriptionContainer} from 'jbc-front/components/presenters/ui/HoverDescription'
import Loading from 'components/Loading'
import {useQuery} from 'hooks/graphql'
import {useFetchApplicableProcedures} from 'hooks/api/procedureStatuses'
import {useFetchProcedureCsvRequest} from 'hooks/api/exportProcedureCsv'
import {QueryResult, EGOV_INFORMATION_COMPLETED} from '../../query'
import styles from './SelectProcedureModal.scss'

// @ts-ignore
import ListGroup from 'jbc-front/components/ListGroup'

type ActionType = 'export_csv' | 'egov'

type Query = {
  procedure_type: string
  status: string
  q: string
  per_page: string
  office_ids?: string[]
}

type ModalProps = {
  csvDownloadable: boolean
  hasBranchOffice: boolean
  selectedOffice?: string
  query: Query
}

export const SelectProcedureModal: FC<ModalProps> = ({csvDownloadable, hasBranchOffice, selectedOffice, query}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isBalloonOpen, setIsBalloonOpen] = useState<boolean>(false)
  const [isHover, setIsHover] = useState<boolean>(false)
  const [isEgovHover, setIsEgovHover] = useState<boolean>(false)
  const [actionType, setActionType] = useState<ActionType>('export_csv')
  const isExportCsv = actionType === 'export_csv'
  const handleToggleOpen = () => setIsBalloonOpen((prev) => !prev)
  const history = useHistory()
  const handleClose = () => setIsOpen(false)
  const {data} = useQuery<QueryResult>(EGOV_INFORMATION_COMPLETED, {fetchPolicy: 'network-only'})
  const egovInformationCompleted = data?.client?.egovInformationCompleted
  const {
    isLoading,
    resource: applicableProcedures,
    getter: applicableProceduresGetter,
  } = useFetchApplicableProcedures(query.procedure_type, actionType, query?.office_ids)
  const {isLoading: isCsvRequestLoading, resource: csvRequest} = useFetchProcedureCsvRequest()
  const working = csvRequest && ['waiting', 'in_progress'].includes(csvRequest.status)
  const onClickCsvProcedure = (procedureKind: string) => {
    history.push({
      pathname: '/procedure_statuses/choose_procedures',
      search: queryString.stringify({
        ...query,
        procedure_type: query.procedure_type,
        status: query.status,
        search_keyword: query.q,
        per_page: query.per_page,
        procedure_kind: procedureKind,
        action_type: actionType,
      }),
    })
  }

  const onClickBalloon = (type: ActionType) => {
    setIsOpen(true)
    setIsBalloonOpen(false)
    setActionType(type)
  }

  useEffect(() => {
    if (isOpen) {
      applicableProceduresGetter()
    }
  }, [isOpen])

  return (
    <>
      <Balloon.Container onClose={() => setIsBalloonOpen(false)}>
        <Button
          primary
          className={styles.eGovButton}
          disabled={!csvDownloadable || (hasBranchOffice && !selectedOffice)}
          onClick={handleToggleOpen}
        >
          電子・電子媒体申請
        </Button>

        <Balloon isOpen={isBalloonOpen}>
          <DescriptionContainer onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
            <HoverDescription isHover={!!working && isHover}>
              作成に時間がかかるため、完了しましたらメールにてお送りいたします。
            </HoverDescription>
            <Balloon.Item disabled={working} onClick={() => onClickBalloon('export_csv')}>
              提出用CSVダウンロード
            </Balloon.Item>
          </DescriptionContainer>

          <DescriptionContainer onMouseEnter={() => setIsEgovHover(true)} onMouseLeave={() => setIsEgovHover(false)}>
            <HoverDescription isHover={!egovInformationCompleted && isEgovHover}>
              電子申請を設定してください
            </HoverDescription>
            <Balloon.Item disabled={!egovInformationCompleted} onClick={() => onClickBalloon('egov')}>
              電子申請
            </Balloon.Item>
          </DescriptionContainer>
        </Balloon>
      </Balloon.Container>

      <Modal isOpen={isOpen} onClose={handleClose}>
        <Modal.Header onClose={handleClose}>{isExportCsv ? '書類作成' : '電子申請する書類'}</Modal.Header>
        <Modal.Body>
          <div>
            {isLoading || isCsvRequestLoading ? (
              <Loading />
            ) : (
              <>
                <h5>{isExportCsv ? '作成する書類を選択してください' : '電子申請する書類を選択してください'}</h5>
                <ListGroup>
                  {applicableProcedures.map((procedure) => (
                    <ListGroup.Item
                      key={procedure.procedure_kind}
                      as="a"
                      onClick={() => onClickCsvProcedure(procedure.procedure_kind)}
                    >
                      {procedure.name}
                      <ListGroup.Icon />
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}
